import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import { IContest } from '../../../api/types';
import LockIcon from '@mui/icons-material/Lock';

interface ContestCardProps {
  contest: IContest;
  progress: number;
  lastUpdated: string;
  onContinue: () => void;
  onRegister: () => void;
  isLocked?: boolean;
  lockMessage?: string;
}

const ContestCard = ({ 
  contest, 
  progress, 
  lastUpdated, 
  onContinue,
  onRegister,
  isLocked,
  lockMessage 
}: ContestCardProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const getButtonConfig = () => {
    switch (contest.participation_status) {
      case 1:
        return {
          text: 'Continue Competition',
          onClick: onContinue,
          disabled: false
        };
      case 2:
        return {
          text: 'Register',
          onClick: onRegister,
          disabled: false
        };
      case 0:
        return {
          text: 'Request Sent',
          onClick: undefined,
          disabled: true
        };
      default:
        return {
          text: 'Register',
          onClick: onRegister,
          disabled: false
        };
    }
  };

  return (
    <Card
      sx={{
        display: "flex",
        p: { xs: 1.5, sm: 2 },
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 2, sm: 0 },
      }}
    >
      <CardMedia
        component="img"
        sx={{
          width: { xs: "100%", sm: 200 },
          height: { xs: 200, sm: 140 },
          objectFit: "cover",
          borderRadius: 1,
        }}
        image={contest.image_url}
        alt={contest.title}
      />
      
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          ml: { xs: 0, sm: 2 },
        }}
      >
        <CardContent sx={{ flex: '1 0 auto', p: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            mb={1}
          >
            <Typography component="div" variant="h6" sx={{
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}>
              {contest.title}
            </Typography>
            <Chip 
              label={contest.level} 
              color={contest.participation_status === 1 ? 'primary' : 'default'}
              size="small"
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
              }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}>
            {contest.excerpt}
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 65 }}>
                Progress:
              </Typography>
              <Box sx={{ flexGrow: 1, mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#DFE3E8',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#00B248',
                      boxShadow: '0 0 4px rgba(0, 178, 72, 0.5)'
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
                {progress}%
              </Typography>
            </Box>
          

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  order: { xs: 2, sm: 1 },
                }}
              >
                Last updated: {lastUpdated}
              </Typography>

              {isLocked ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    order: { xs: 1, sm: 2 },
                  }}
                >
                  <LockIcon color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {lockMessage}
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={getButtonConfig().onClick}
                  disabled={getButtonConfig().disabled}
                  fullWidth={isMobile}
                  sx={{
                    order: { xs: 1, sm: 2 },
                  }}
                >
                  {getButtonConfig().text}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Box>
    </Card>
  );
};

export default ContestCard; 